import * as THREE from 'three/webgpu';

/** 分量线退化判定容差（世界单位）。 */
const MEASURE_DX_DY_DZ_SEGMENT_LENGTH_EPSILON = 1e-9;

/** 点重合判定容差平方。 */
const MEASURE_DX_DY_DZ_POINT_EQUAL_EPSILON_SQ = MEASURE_DX_DY_DZ_SEGMENT_LENGTH_EPSILON
    * MEASURE_DX_DY_DZ_SEGMENT_LENGTH_EPSILON;

/**
 * 可展示分量线种类。
 */
export enum MeasureDxDyDzRenderableSegmentKind {
    /** X 方向分量线。 */
    Dx = 'dx',
    /** Y 方向分量线。 */
    Dy = 'dy',
    /** Z 方向分量线。 */
    Dz = 'dz',
}

/**
 * 可展示分量线段。
 */
export interface MeasureDxDyDzRenderableSegment {
    /** 分量线种类。 */
    kind: MeasureDxDyDzRenderableSegmentKind;
    /** 线段起点。 */
    start: THREE.Vector3;
    /** 线段终点。 */
    end: THREE.Vector3;
}

/**
 * 解析可展示 Dx/Dy/Dz 分量线输入。
 */
export interface ResolveRenderableDxDyDzSegmentsInput {
    /** 直线距离起点。 */
    from: THREE.Vector3;
    /** 直线距离终点。 */
    to: THREE.Vector3;
    /** X 方向分量差。 */
    dx: number;
    /** Y 方向分量差。 */
    dy: number;
    /** Z 方向分量差。 */
    dz: number;
}

/**
 * 判断线段是否具有可渲染长度。
 *
 * @param start 起点。
 * @param end 终点。
 * @returns 是否具有可渲染长度。
 */
export function hasRenderableMeasureSegmentLength(start: THREE.Vector3, end: THREE.Vector3): boolean {
    const dx = end.x - start.x;
    const dy = end.y - start.y;
    const dz = end.z - start.z;
    return dx * dx + dy * dy + dz * dz > MEASURE_DX_DY_DZ_POINT_EQUAL_EPSILON_SQ;
}

/**
 * 解析实际应展示的 Dx/Dy/Dz 分量线。
 *
 * 说明：当唯一非退化分量线与直线距离线完全重合时，SolidWorks 仅展示距离线与距离浮窗。
 *
 * @param input 分量线输入。
 * @returns 可展示分量线列表。
 */
export function resolveRenderableDxDyDzSegments(
    input: ResolveRenderableDxDyDzSegmentsInput,
): MeasureDxDyDzRenderableSegment[] {
    const { from, to, dx, dy, dz } = input;
    const cornerAfterDx = new THREE.Vector3(from.x + dx, from.y, from.z);
    const cornerAfterDy = new THREE.Vector3(from.x + dx, from.y + dy, from.z);

    const candidates: MeasureDxDyDzRenderableSegment[] = [
        {
            kind: MeasureDxDyDzRenderableSegmentKind.Dx,
            start: from.clone(),
            end: cornerAfterDx,
        },
        {
            kind: MeasureDxDyDzRenderableSegmentKind.Dy,
            start: cornerAfterDx.clone(),
            end: cornerAfterDy,
        },
        {
            kind: MeasureDxDyDzRenderableSegmentKind.Dz,
            start: cornerAfterDy.clone(),
            end: to.clone(),
        },
    ].filter((segment) => hasRenderableMeasureSegmentLength(segment.start, segment.end));

    if (candidates.length === 1 && isSameSegment(candidates[0]!, from, to)) {
        return [];
    }

    return candidates;
}

/**
 * 判断两个线段是否同向或反向重合。
 *
 * @param segment 待判断线段。
 * @param start 参考线段起点。
 * @param end 参考线段终点。
 * @returns 是否重合。
 */
function isSameSegment(
    segment: MeasureDxDyDzRenderableSegment,
    start: THREE.Vector3,
    end: THREE.Vector3,
): boolean {
    return (
        isSamePoint(segment.start, start)
        && isSamePoint(segment.end, end)
    ) || (
        isSamePoint(segment.start, end)
        && isSamePoint(segment.end, start)
    );
}

/**
 * 判断两点是否可视为重合。
 *
 * @param first 第一个点。
 * @param second 第二个点。
 * @returns 是否重合。
 */
function isSamePoint(first: THREE.Vector3, second: THREE.Vector3): boolean {
    return first.distanceToSquared(second) <= MEASURE_DX_DY_DZ_POINT_EQUAL_EPSILON_SQ;
}
